import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, Check, ChevronDown, Download } from "lucide-react";
import { SmartlaneButton } from "@/components/SmartlaneButton";
import { LogoCloud } from "@/components/LogoCloud";
import { FaqAccordion, faqJsonLd } from "@/components/FaqAccordion";
import { ContactForm } from "@/components/ContactForm";
import { UsageModelSwitcher } from "@/components/UsageModelSwitcher";
import { FeatureSlider } from "@/components/FeatureSlider";
import { CustomerCaseSlider } from "@/components/CustomerCaseSlider";
import { TeaserVideo, type TeaserCue } from "@/components/TeaserVideo";
import { Reveal } from "@/components/Reveal";
import { usePrefersReducedMotion } from "@/hooks/useInView";
import { absoluteUrl } from "@/lib/site";
import { canonicalLink, hreflangLinks } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";
import { IDO_ACTIONS, IDO_FEATURE_SLIDES } from "@/data/interaktive-tourenplanung-content";
import { TPS_FAQS, TPS_STEPS, TPS_BENEFITS } from "@/data/tourenplanungssoftware-content";


import heroTimelineScreenshot from "@/assets/screenshots/smartlane-timeline-setup.png.asset.json";
import productPdf from "@/assets/downloads/Smartlane_Produktinformation.pdf.asset.json";
import teaserVideo from "@/assets/video/STI_Produktvideo-hq.webm.asset.json";
import teaserPoster from "@/assets/video/STI_Produktvideo-poster.jpg.asset.json";

const CANONICAL = absoluteUrl("/interaktive-tourenplanungssoftware");

const softwareJsonLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Smartlane Transport Intelligence – Interaktive Tourenplanung",
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "Route Planning & Dispatch Software",
  operatingSystem: "Web",
  inLanguage: "de-DE",
  featureList: IDO_ACTIONS.map((a) => a.title),
  description:
    "Interaktive Tourenplanungssoftware mit KI-Optimierung: einsetzbar mit oder ohne TMS, per Datei-Upload, automatischem Dateiaustausch oder direkter Anbindung. Tourenvorschläge direkt in der Oberfläche prüfen, bearbeiten und exportieren.",

  url: CANONICAL,
  offers: {
    "@type": "Offer",
    priceCurrency: "EUR",
    price: "0",
    description: "Kostenfreie Potentialanalyse mit eigenen Daten",
  },
  provider: {
    "@type": "Organization",
    name: "Smartlane GmbH",
    url: absoluteUrl("/"),
    address: {
      "@type": "PostalAddress",
      streetAddress: "Gotzinger Straße 8",
      addressLocality: "München",
      postalCode: "81371",
      addressCountry: "DE",
    },
  },
};

export const Route = createFileRoute("/interaktive-tourenplanungssoftware")({
  head: () => ({
    meta: [
      { title: "Tourenplanungssoftware für Spedition & Handel | Smartlane" },
      {
        name: "description",
        content:
          "Smartlane plant Ihre täglichen Touren automatisch – für Spedition, Großhandel, Filialbelieferung und Distribution. Mit oder ohne TMS, jederzeit steuerbar.",
      },
      {
        property: "og:title",
        content: "Tourenplanungssoftware für Spedition & Handel | Smartlane",
      },
      {
        property: "og:description",
        content:
          "Automatisch optimierte Touren in Minuten: Smartlane Transport Intelligence berücksichtigt Ihre Restriktionen und lässt sich mit oder ohne TMS einsetzen.",
      },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: heroTimelineScreenshot.url },
      { name: "twitter:image", content: heroTimelineScreenshot.url },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      canonicalLink("/interaktive-tourenplanungssoftware"),
      ...hreflangLinks("/interaktive-tourenplanungssoftware"),
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(softwareJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd(TPS_FAQS)) },
    ],
  }),
  component: TourenplanungssoftwarePage,
});



function TourenplanungssoftwarePage() {
  const sliderInterestTracked = useRef(false);

  return (
    <main className="campaign-page min-w-0">

      {/* Opening: Botschaft und Produkt gehören zusammen */}
      <div className="relative bg-rich-black">
        <Hero />
        <DemoSection />
      </div>

      {/* Helle Produkt- und Setup-Welt */}
      <div className="campaign-trust relative bg-white pt-28 md:pt-40">
        <LogoCloud compact logoSpacing="wide" title="FÜHRENDE UNTERNEHMEN SETZEN AUF SMARTLANE" />
      </div>

      <div className="relative bg-white">
        <HowItWorksSection />
        <BenefitsSection />
      </div>

      <CustomerCaseSlider onNavigate={(segment, customer, method) => {
        if (method !== "pfeil" || sliderInterestTracked.current) return;
        sliderInterestTracked.current = true;
        trackEvent("kundenslider_geklickt", { segment, customer, method });
      }} />

      <div className="relative bg-white">
        <FeatureSlider
          items={IDO_FEATURE_SLIDES}
          eyebrow="FUNKTIONEN IM ÜBERBLICK"
          heading="GEBAUT FÜR DEN DISPO-ALLTAG. WEIL TOURENPLANUNG SELTEN NACH STANDARD VERLÄUFT."
          intro="Smartlane Transport Intelligence verbindet KI-basierte Optimierung mit einer interaktiven Oberfläche, in der Ihre Disposition Touren prüft, anpasst und freigibt. Wir entwickeln die Software kontinuierlich weiter – eng abgestimmt mit unseren Kunden. Ein Auszug wichtiger Funktionen:"
          footer={<ProductDownloadButton />}
        />

        <SetupSection />
        <NotATmsSection />
        <ContactSection />
        <FaqSection />
      </div>
    </main>
  );
}

/* ─────────────── Produktinformation ─────────────── */
function ProductDownloadButton() {
  return (
    <SmartlaneButton
      href={productPdf.url}
      target="_blank"
      rel="noopener noreferrer"
      variant="ghost"
      size="sm"
      onClick={() => trackEvent("download_produktinformation", { file_name: "Smartlane Produktinformation", form_location: "interaktive-tourenplanungssoftware" })}
      className="w-full border border-rich-black/15 text-rich-black/80 sm:w-auto"
    >
      <Download className="h-4 w-4" />
      Produktinformation herunterladen
    </SmartlaneButton>
  );
}

/* ─────────────── Hero ─────────────── */

function Hero() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [offset, setOffset] = useState(0);
  const sectionRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (prefersReducedMotion) {
      setOffset(0);
      return;
    }
    let frame = 0;
    const update = () => {
      frame = 0;
      const element = sectionRef.current;
      if (!element) return;
      const rect = element.getBoundingClientRect();
      if (rect.bottom < 0) return;
      setOffset(Math.min(120, Math.max(0, -rect.top) * 0.18));
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [prefersReducedMotion]);

  return (
    <section ref={sectionRef} className="campaign-hero relative isolate flex min-h-[88svh] items-center overflow-hidden">
      <img
        src={heroTimelineScreenshot.url}
        alt="Smartlane Oberfläche mit Karte, Tourenliste und Zeitachse"
        loading="eager"
        decoding="async"
        style={{ transform: `translate3d(0, ${offset}px, 0) scale(1.08)` }}
        className="absolute inset-0 h-full w-full object-cover opacity-55 will-change-transform"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-rich-black/75 via-rich-black/45 to-rich-black" />
      <div className="pointer-events-none absolute inset-0 bg-dot-grid-dark opacity-25" />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: "radial-gradient(60% 50% at 70% 30%, hsl(var(--smartlane-orange) / 0.18), transparent 60%)",
        }}
      />

      <div className="campaign-hero-content container-wide relative z-10 pb-16 pt-28 md:pb-24 md:pt-32 xl:pb-28 xl:pt-40">
        <div className="max-w-6xl">
          <div className="mb-8 inline-flex max-w-full items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 backdrop-blur-md">
            <span className="h-2 w-2 shrink-0 rounded-full bg-smartlane-orange" />
            <span className="min-w-0 font-body text-xs uppercase tracking-[0.2em] text-white">
              TOURENPLANUNG FÜR SPEDITION, HANDEL & DISTRIBUTION
            </span>
          </div>

          <h1 className="text-display-lg mb-8 text-balance uppercase leading-[0.95] tracking-wider text-white">
            JEDEN TAG OPTIMIERTE TOUREN:{" "}
            <span className="text-smartlane-orange">AUTOMATISCH GEPLANT, JEDERZEIT STEUERBAR.</span>
          </h1>

          <p className="copy mb-10 max-w-2xl text-lg leading-relaxed text-white md:text-xl">
            Smartlane Transport Intelligence ist eine Software für die automatische Tourenplanung. Sie erstellt aus
            Ihren Aufträgen in Minuten realistische, optimierte Touren – unter Berücksichtigung Ihrer Fahrzeuge,
            Zeitfenster und Restriktionen. Einsetzbar als Ergänzung zum TMS oder als Stand-alone-Lösung.{"\u00a0"}
          </p>

          <div className="flex flex-col gap-4 sm:flex-row">
            <SmartlaneButton
              href="#kontakt"
              variant="primary"
              size="lg"
              onClick={() => trackEvent("cta_click", { cta_location: "hero", cta_label: "Gespräch vereinbaren" })}
            >
              GESPRÄCH VEREINBAREN <ArrowRight className="h-5 w-5" />
            </SmartlaneButton>
            <SmartlaneButton
              href="#so-funktioniert-es"
              variant="ghost"
              size="lg"
              className="border border-white/25 text-white"
              onClick={() => trackEvent("cta_click", { cta_location: "hero", cta_label: "So funktioniert es" })}
            >
              SO FUNKTIONIERT ES
            </SmartlaneButton>
          </div>

        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 text-white/50 md:flex">
        <span className="font-body text-[10px] uppercase tracking-[0.3em]">Mehr erfahren</span>
        <ChevronDown className="h-5 w-5 animate-bounce" />
      </div>
    </section>
  );
}

/* ─────────────── 90-Sekunden-Demo (Teil des Openings) ─────────────── */
function DemoSection() {
  const teaserCues: TeaserCue[] = [
    { text: "Relevante Informationsebenen ein- und ausblenden", start: 1, end: 7 },
    { text: "Nicht disponierte Aufträge auf der Karte einblenden", start: 8, end: 12 },
    { text: "Aufträge filtern und sortieren", start: 13, end: 19 },
    { text: "Aufträge per Drag & Drop von Touren entfernen oder hinzufügen", start: 40, end: 47 },
    { text: "Änderungen rückgängig machen", start: 65, end: 70.1 },
  ];

  return (
    <section className="campaign-demo relative pb-10 pt-4 md:pb-14 md:pt-8">
      <div className="container-wide max-w-[1680px]">
        <div className="campaign-demo-overlap relative z-10 -mb-20 md:-mb-32">
          <TeaserVideo
            src={teaserVideo.url}
            poster={teaserPoster.url}
            cues={teaserCues}
            ariaLabel="Smartlane Transport Intelligence – Demo-Vorschau"
            onPlayStart={() => trackEvent("video_start", { video_title: "Produktvideo Dispositionsoberfläche" })}
            onComplete={() => trackEvent("video_complete", { video_title: "Produktvideo Dispositionsoberfläche" })}
            className="teaser-video-shell relative mx-auto aspect-[1918/940] w-full shadow-[0_60px_120px_-50px_rgba(0,0,0,0.85)]"
          />

        </div>
      </div>
    </section>
  );
}

/* ─────────────── Was Smartlane macht ─────────────── */
function HowItWorksSection() {
  return (
    <section id="so-funktioniert-es" className="relative scroll-mt-20 py-16 md:py-24">
      <div className="container-wide">
        <Reveal>
          <p className="eyebrow">So funktioniert es</p>
          <h2 className="text-display-md mt-3 max-w-4xl text-rich-black">
            VON DER AUFTRAGSLISTE ZUR <span className="text-smartlane-orange">FERTIGEN TOUR.</span>
          </h2>
          <p className="copy mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">
            Smartlane übernimmt den zeitaufwendigsten Teil der Disposition: das Planen der Aufträge auf den zur Verfügung stehenden Fuhrpark unter Einhaltung aller relevanten Planungsrestriktionen. Ihre
            Disponentinnen und Disponenten behalten dabei die Entscheidung.
          </p>
        </Reveal>
        <ol className="mt-12 grid gap-6 md:grid-cols-3">
          {TPS_STEPS.map((step, i) => (
            <Reveal key={step.title} delay={i * 100}>
              <li className="h-full border-t-2 border-smartlane-orange pt-5">
                <span className="font-heading text-4xl text-smartlane-orange">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-heading text-xl uppercase tracking-wide text-rich-black md:text-2xl">{step.title}</h3>
                <p className="copy mt-3 text-base text-rich-black/70">{step.text}</p>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ─────────────── Mehrwert ─────────────── */
function BenefitsSection() {
  return (
    <section className="relative border-y border-rich-black/[0.05] bg-surface-soft py-16 md:py-24">
      <div className="container-wide">
        <Reveal>
          <p className="eyebrow">Ihr Mehrwert</p>
          <h2 className="text-display-md mt-3 max-w-4xl text-rich-black">
            WAS SICH IN IHRER <span className="text-smartlane-orange">DISPOSITION VERÄNDERT.</span>
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-x-12 gap-y-10 md:grid-cols-2">
          {TPS_BENEFITS.map((b, i) => (
            <Reveal key={b.title} delay={(i % 2) * 100}>
              <div className="flex items-start gap-4">
                <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-smartlane-orange text-white">
                  <Check className="h-4 w-4" strokeWidth={3} />
                </span>
                <div>
                  <h3 className="font-heading text-xl uppercase tracking-wide text-rich-black">{b.title}</h3>
                  <p className="copy mt-2 text-base text-rich-black/70">{b.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────── Nutzungsmodelle ─────────────── */
function SetupSection() {
  return (
    <section
      id="modelle"
      className="relative scroll-mt-20 border-y border-rich-black/[0.05] bg-surface-soft py-20 md:py-28"
    >
      <div className="container-wide relative" id="modell-finden">
        <Reveal>
          <p className="eyebrow">Nutzungsmodelle</p>
          <h2 className="text-display-md mt-3 max-w-4xl text-rich-black">
            MIT TMS. OHNE TMS. <span className="text-smartlane-orange">MIT ODER OHNE SCHNITTSTELLE.</span>
          </h2>
          <p className="copy mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">
            Die Oberfläche ist ein Angebot, kein Muss: Wer im TMS bleiben möchte, bleibt im TMS. Die Einführung
            erfolgt ohne Prozessanpassungen auf Ihrer Seite – viele Wege, ein Ziel: täglich optimierte Touren.
          </p>
        </Reveal>
        <UsageModelSwitcher />
      </div>
    </section>
  );
}

/* ─────────────── Klarstellung: kein TMS-Ersatz ─────────────── */
const SMARTLANE_IST = [
  "KI-basierte Tourenplanung und -optimierung",
  "Grafische Feinjustierung in der Weboberfläche",
  "Flexible Nutzung mit oder ohne TMS",
  "Vielfältige Konfigurationsmöglichkeiten für Ihre Planungsregeln",
  "Premium-Komplettpaket: von der Anbindung über die Optimierung bis zur persönlichen Betreuung",
];

function NotATmsSection() {
  return (
    <section className="campaign-specialist relative border-t border-rich-black/10 py-20 md:py-24">
      <div className="container-wide">
        <div className="grid min-w-0 items-start gap-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-12 xl:gap-20">
          <Reveal>
            <p className="eyebrow">Klar eingeordnet</p>
            <h2 className="font-heading mt-4 text-3xl uppercase leading-tight tracking-wide text-rich-black md:text-4xl">
              Smartlane bleibt Spezialist für Tourenplanung – <span className="text-smartlane-orange">kein TMS.</span>
            </h2>
            <p className="copy mt-6 max-w-xl text-base text-rich-black/70 md:text-lg">
              Mit der interaktiven Weboberfläche wird Smartlane flexibler im Einsatz – mit oder ganz ohne TMS. Was wir
              am besten können, bleibt: eine intelligente Tourenplanung, die Ihr System ergänzt statt ersetzt. Als
              spezialisierte Expertenlösung liefern wir realistische, gleichzeitig optimierte Touren – und begleiten
              die Einführung persönlich, ohne dass Sie Ihre Prozesse anpassen müssen.
            </p>
          </Reveal>

          <Reveal delay={120} className="grid gap-10">
            <div>
              <p className="border-b border-rich-black/15 pb-3 font-body text-xs font-semibold uppercase tracking-[0.2em] text-smartlane-orange">
                Smartlane bietet
              </p>
              <ul className="mt-4 space-y-3">
                {SMARTLANE_IST.map((item) => (
                  <li key={item} className="flex items-start gap-3 border-b border-rich-black/5 pb-3">
                    <Check className="mt-1 h-4 w-4 shrink-0 text-smartlane-orange" strokeWidth={3} />
                    <span className="copy text-base text-rich-black">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ─────────────── Kontakt ─────────────── */
function ContactSection() {
  return (
    <section className="campaign-contact relative border-t border-rich-black/10 py-20 md:py-28">
      <div className="container-wide relative">
        <div className="grid min-w-0 items-start gap-8 lg:grid-cols-2 lg:gap-12 xl:gap-20">
          <Reveal>
            <p className="eyebrow">Kontakt</p>
            <h2 className="text-display-md mt-3 text-rich-black">
              JETZT <span className="text-smartlane-orange">KOSTENFREI STARTEN.</span>
            </h2>
            <p className="copy mt-5 max-w-xl text-base text-muted-foreground md:text-lg">
              Sie möchten Ihren Anwendungsfall besprechen und in einem kostenfreien Testlauf evaluieren, wie viel
              Potenzial in Ihren Touren steckt? Schreiben Sie uns – wir freuen uns auf Ihre Anfrage.
            </p>
            <ul className="mt-10 space-y-4">
              {[
                "Unverbindliches Kennenlerngespräch zu Ihrer heutigen Tourenplanung",
                "Kostenfreie Potentialanalyse mit Ihren echten Daten",
                "Beratung zum passenden Nutzungsmodell und wie Smartlane Transport Intelligence ohne Prozessanpassungen Ihrerseits eingeführt werden kann",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-smartlane-orange text-white">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </span>
                  <span className="copy text-base text-rich-black">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <div id="kontakt" className="scroll-mt-28">
            <ContactForm
              heading="Anfrage senden"
              onFormStart={() => trackEvent("contact_form_started", { form_location: "interaktive-tourenplanungssoftware" })}
              onSubmitSuccess={() => trackEvent("kontaktformular_abgesendet", { form_location: "interaktive-tourenplanungssoftware" })}
            />

          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────── FAQ ─────────────── */
function FaqSection() {
  return (
    <section className="campaign-faq py-16 md:py-20">
      <div className="container-wide max-w-4xl">
        <Reveal>
          <p className="eyebrow">Häufige Fragen</p>
          <h2 className="font-heading mt-3 text-2xl uppercase tracking-wide text-rich-black md:text-3xl">
            Antworten auf die wichtigsten Fragen.
          </h2>
        </Reveal>
        <div className="mt-10">
          <FaqAccordion items={TPS_FAQS} onOpen={(question) => trackEvent("faq_opened", { question })} />
        </div>

        <div className="mt-12 flex flex-col items-center gap-4 rounded-2xl border border-rich-black/10 bg-surface-soft px-6 py-10 text-center">
          <h3 className="font-heading text-xl uppercase tracking-wide text-rich-black md:text-2xl">
            Ihre Frage war nicht dabei?
          </h3>
          <p className="copy max-w-xl text-base text-muted-foreground">
            Sprechen Sie direkt mit unseren Expertinnen und Experten – wir beantworten Ihre Fragen zu Einsatz,
            Anbindung und Potenzial in Ihrer Disposition.
          </p>
          <SmartlaneButton
            href="#kontakt"
            variant="primary"
            size="md"
            className="mt-2"
            onClick={() => trackEvent("cta_click", { cta_location: "faq", cta_label: "Jetzt Kontakt aufnehmen" })}
          >
            Jetzt Kontakt aufnehmen <ArrowRight className="h-4 w-4" />
          </SmartlaneButton>

        </div>
      </div>
    </section>
  );
}
